import { type JSX, type ReactNode, createContext, use, useMemo } from 'react'

import { usePreferences } from '../hooks/use-preferences.ts'
import { updatePreferences } from '../lib/preference-store.ts'
import { parseTimeZone, UTC_TIME_ZONE } from '../lib/time-zone.ts'

/** What `useTimeZone` hands a consumer. */
export interface TimeZoneContextValue {
  /** The zone in force. An unset choice is UTC. */
  timeZone: string
  /** Hold a zone, and apply it to this tab. */
  setTimeZone: (timeZone: string) => void
}

/**
 * Hold a zone in the reader's settings. A name this console does not apply is
 * ignored, so a bad value can reach neither the cache nor the server.
 *
 * @param next - The zone the reader chose.
 */
function holdTimeZone(next: string): void {
  const zone = parseTimeZone(next)
  if (zone !== null) {
    void updatePreferences({ timeZone: zone })
  }
}

const TimeZoneContext = createContext<TimeZoneContextValue>({
  timeZone: UTC_TIME_ZONE,
  setTimeZone: holdTimeZone
})

/**
 * Read the reader's zone, and change it.
 *
 * Outside a provider this is UTC, and a write still persists.
 * A screen test that renders no provider keeps working. The rail sets the
 * zone through the provider, so a change re-renders every report.
 *
 * @returns The zone context.
 */
export function useTimeZone(): TimeZoneContextValue {
  return use(TimeZoneContext)
}

/**
 * TimeZoneProvider — the reader's zone for every report.
 *
 * Sits beside the theme provider, above both the signed-in app and the
 * sign-in screen, so signing in does not remount it and drop the choice.
 *
 * @param props - Provider props.
 * @param props.children - The app.
 * @returns The provider.
 */
export function TimeZoneProvider({
  children
}: {
  children: ReactNode
}): JSX.Element {
  const timeZone = usePreferences().timeZone ?? UTC_TIME_ZONE
  const value = useMemo(
    () => ({ timeZone, setTimeZone: holdTimeZone }),
    [timeZone]
  )

  return <TimeZoneContext value={value}>{children}</TimeZoneContext>
}
